import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import styled from "styled-components";
import { Modal } from "../../common-components/modal";
import ShortsWrapper from "./shorts-wrapper";
import ShortsList from "./shorts-list";
import ShortsPreset from "./shorts-preset";
import ShortsDislikeModal from "./shorts-dislike-modal";
import { PLAN } from "../../constants";
import content from "../../constants/content";
import { API, API_URL, STATIC_PATH } from "../../constants/config";
import useApi from "../../helper/hooks/useApi";
import useNotify from "../../helper/hooks/useNotify";
import { useShortsContext } from "./shortsContextApi";
import contextActionType from "./context-action-type";
import { setBackdropLoader } from "../../redux/actions/appUtils";
import BackdropLoader from "../../common-components/BackdropLoader";
import openUrl from "../../helper/openUrl";
import useUpgrade from "../../helper/hooks/useUpgrade";


const { CLOSE_SHORT_PRESET, GET_PRESET_MODAL_OPEN, GET_PLAYER_REMOUNT_KEY } = contextActionType;

const BannerWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  width: 100%;
  box-sizing: border-box;
`;

const BannerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border-radius: 20px;
  padding: 20px 24px;
  margin: 24px 0 0 12px;
  max-width: 1080px;
  width: calc(100% - 24px);
  box-shadow: 0px 12px 32px rgba(0, 0, 0, 0.08), 0px 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-sizing: border-box;

  @media (max-width: 1136px) {
    margin: 24px auto 0;
    width: calc(100% - 24px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 20px;
    margin: 16px auto 0;
    width: calc(100% - 24px);
  }
`;

const BannerLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BannerTitle = styled.h2`
  font-family: "VM-Poppins", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  padding: 0;
  line-height: 1.2;
`;

const BannerSubtitle = styled.div`
  display: flex;
  align-items: center;
  font-family: "VM-Poppins", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  line-height: 1.5;

  @media (max-width: 480px) {
    align-items: flex-start;
  }
`;

const UpgradeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(90deg, #ff7e40 0%, #ff451a 100%);
  color: #ffffff;
  border: none;
  border-radius: 100px;
  padding: 12px 24px;
  font-family: "VM-Poppins", sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0px 4px 12px rgba(255, 69, 26, 0.2);
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0px 6px 16px rgba(255, 69, 26, 0.3);
    filter: brightness(1.05);
  }

  &:active {
    transform: translateY(0);
    filter: brightness(0.95);
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const FEEDBACK_TYPE = {
  LIKE: "like",
  UNDO_LIKE: "undo_like",
  DISLIKE: "dislike",
  UNDO_DISLIKE: "undo_dislike",
};

const isActiveFeedbackType = (type) =>
  type === FEEDBACK_TYPE.LIKE || type === FEEDBACK_TYPE.DISLIKE;

const isUndoFeedbackType = (type) =>
  type === FEEDBACK_TYPE.UNDO_LIKE || type === FEEDBACK_TYPE.UNDO_DISLIKE;

const getFeedbackStorageKey = (projectId) => `vm-shorts-feedback-${projectId}`;

const readFeedbackCache = (projectId) => {
  if (!projectId) return { map: {}, hasCache: false };
  try {
    const raw = sessionStorage.getItem(getFeedbackStorageKey(projectId));
    if (raw) {
      return { map: JSON.parse(raw), hasCache: true };
    }
  } catch {
    // ignore invalid cache
  }
  return { map: {}, hasCache: false };
};

const writeFeedbackCache = (projectId, map) => {
  if (!projectId) return;
  try {
    sessionStorage.setItem(getFeedbackStorageKey(projectId), JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
};

const ShortsListModal = ({ shorts, projectDetails, userDetails, params, setBackdropLoader, backdropLoader }) => {
  const { plan, projectId } = userDetails;
  const initialFeedbackCache = useMemo(() => readFeedbackCache(projectId), [projectId]);
  const [activeShort, setActiveShort] = useState(null);
  const [modalScrollKey, setModalScrollKey] = useState(0);
  const [viewedShortIds, setViewedShortIds] = useState(new Set());
  const [feedbackMap, setFeedbackMap] = useState(initialFeedbackCache.map);
  const [isFeedbackLoaded, setIsFeedbackLoaded] = useState(initialFeedbackCache.hasCache);
  const [dislikeShortId, setDislikeShortId] = useState(null);
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const { getShortsContext, setShortsContext } = useShortsContext();
  const presetModalOpen = getShortsContext(GET_PRESET_MODAL_OPEN);
  const playerRemountKey = getShortsContext(GET_PLAYER_REMOUNT_KEY);
  const { post } = useApi();
  const { success } = useNotify();
  const { showPricingModal } = useUpgrade();
  const intl = useIntl();

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;

    post(`${API_URL}${API.SHORTS.FEEDBACK_PROJECT}`, { projectId })
      .then((res) => {
        if (cancelled) return;
        if (res.error || !res.shorts) return;
        const map = {};
        res.shorts.forEach((item) => {
          if (item.shortId) {
            if (!isActiveFeedbackType(item.feedbackType)) return;
            map[item.shortId] = {
              feedbackType: item.feedbackType,
              feedbackText: item.feedbackText || "",
            };
          }
        });
        setFeedbackMap(map);
        writeFeedbackCache(projectId, map);
      })
      .finally(() => {
        if (!cancelled) {
          setIsFeedbackLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- post from useApi is not stable across renders
  }, [projectId]);

  const saveFeedback = useCallback(
    (shortId, feedbackType, feedbackText = "") => {
      if (!projectId || !shortId) return Promise.resolve();
      setIsSavingFeedback(true);
      const payload = { projectId, shortId, feedbackType, feedbackText };
      return post(`${API_URL}${API.SHORTS.FEEDBACK_SAVE}`, payload)
        .then((res) => {
          if (!res.error) {
            if (isUndoFeedbackType(feedbackType)) {
              setFeedbackMap((prev) => {
                const next = { ...prev };
                delete next[shortId];
                writeFeedbackCache(projectId, next);
                return next;
              });
            } else {
              setFeedbackMap((prev) => {
                const next = {
                  ...prev,
                  [shortId]: { feedbackType, feedbackText },
                };
                writeFeedbackCache(projectId, next);
                return next;
              });
            }
          }
          return res;
        })
        .finally(() => {
          setIsSavingFeedback(false);
        });
    },
    [post, projectId],
  );

  const handleLike = useCallback(
    (shortId) => {
      const current = feedbackMap[shortId]?.feedbackType;
      if (current === FEEDBACK_TYPE.LIKE) {
        saveFeedback(shortId, FEEDBACK_TYPE.UNDO_LIKE);
        return;
      }
      saveFeedback(shortId, FEEDBACK_TYPE.LIKE).then((res) => {
        if (!res?.error) {
          success("Thanks for the feedback");
        }
      });
    },
    [feedbackMap, saveFeedback, success],
  );

  const handleDislikeClick = useCallback((shortId) => {
    if (feedbackMap[shortId]?.feedbackType === FEEDBACK_TYPE.DISLIKE) {
      const { feedbackText = "" } = feedbackMap[shortId] || {};
      saveFeedback(shortId, FEEDBACK_TYPE.UNDO_DISLIKE, feedbackText);
      return;
    }
    setDislikeShortId(shortId);
    setShortsContext(CLOSE_SHORT_PRESET);
  }, [feedbackMap, saveFeedback, setShortsContext]);

  const handleDislikeClose = useCallback(() => {
    setDislikeShortId(null);
  }, []);

  const handleDislikeSubmit = useCallback(
    (feedbackText) => {
      if (!dislikeShortId) return;
      const submittedShortId = dislikeShortId;
      saveFeedback(submittedShortId, FEEDBACK_TYPE.DISLIKE, feedbackText).then((res) => {
        if (!res.error) {
          success("Thanks for the feedback");
          setDislikeShortId(null);
          setActiveShort(null);
        }
      });
    },
    [dislikeShortId, saveFeedback, success],
  );

  const handleSelectShort = useCallback(
    (short) => {
      const shortId = short.get ? short.get("id") : short.id;
      setViewedShortIds((prev) => new Set([...prev, shortId]));
      setActiveShort(short);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setActiveShort(null);
    setModalScrollKey((key) => key + 1);
    setShortsContext(CLOSE_SHORT_PRESET);
  }, [setShortsContext]);

  const handleClosePreset = useCallback(() => {
    setShortsContext(CLOSE_SHORT_PRESET);
  }, [setShortsContext]);

  const handleUpgradeClick = useCallback(() => {
    showPricingModal({ type: "longtoshort", source: "longtoshort" });
  }, [showPricingModal]);

  const { sourceType } = userDetails;
  const isDummyProject = sourceType === "shorts-dummy";
  const activeShortId = activeShort?.get ? activeShort.get("id") : activeShort?.id;


  useEffect(() => {
    if (sourceType === "shorts-dummy" && plan !== PLAN.LITE) {
      setBackdropLoader({
        isLoading: true,
        loaderMessage: "Generating the project...",
      });
      post(`${API_URL}${API.SHORTS.DUMMY_REGENERATE}`, { projectId }).then((res) => {
        openUrl(`${API_URL}dashboard/long-to-short/upload/${res.docId}/${res.taskId}`, true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, plan, projectId])

  return (
    <>
      {isDummyProject && (
        <BannerWrapper>
          <BannerContainer>
            <BannerLeft>
              <BannerTitle>
                {intl.formatMessage({ id: content.FREE_CREDITS_EXHAUSTED })}
              </BannerTitle>
              <BannerSubtitle>
                <img src={`${STATIC_PATH}video-clip.svg`} alt="" style={{ marginRight: '8px', flexShrink: 0, width: '18px', height: '18px' }} />
                <span>
                  {plan === PLAN.LITE
                    ? "2 shorts are predicted to perform exceptionally well. Upgrade to view and publish your shorts."
                    : "Upgrade your plan now, or wait until your credits refresh on your next billing date."}
                </span>
              </BannerSubtitle>
            </BannerLeft>
            <UpgradeButton onClick={handleUpgradeClick}>
              <img src={`${STATIC_PATH}white-star.svg`} alt="" />
              Unlock Shorts
            </UpgradeButton>
          </BannerContainer>
        </BannerWrapper>
      )}
      <ShortsList
        shorts={shorts}
        projectDetails={projectDetails}
        onSelectShort={handleSelectShort}
        viewedShortIds={viewedShortIds}
        feedbackMap={feedbackMap}
        isFeedbackLoaded={isFeedbackLoaded}
        onLike={handleLike}
        onDislike={handleDislikeClick}
      />

      <Modal
        key={`shorts-player-modal-${modalScrollKey}`}
        showModal={!!activeShort && !dislikeShortId}
        onClose={handleCloseModal}
        showBackdrop
        width="auto"
        height="630px"
        maxHeight="630px"
        smHeight="auto"
        overflow="hidden"
        smOverflow="auto"
        mdOverflow="auto"
        closeIconMobRight={"8px"}
        borderRadius="12px"
        closeIconPath={'vm-close-new.svg'}
        closeIconWidth="24px"
        closeIconHeight="24px"
        closeIconRight="12px"
        closeIconTop="12px"
        closeIconMobTop="1%"
        isDisabled
      >
        {activeShort && (
          <ShortsWrapper
            key={playerRemountKey}
            inModal
            params={params}
            projectDetails={projectDetails}
            userDetails={userDetails}
            shortData={activeShort}
            feedbackType={feedbackMap[activeShortId]?.feedbackType || null}
            onLike={() => handleLike(activeShortId)}
            onDislike={() => handleDislikeClick(activeShortId)}
          />
        )}
      </Modal>
      <ShortsPreset
        isShowModal={presetModalOpen}
        onClose={handleClosePreset}
        showBackdrop
      />
      <ShortsDislikeModal
        showModal={!!dislikeShortId}
        onClose={handleDislikeClose}
        onSubmit={handleDislikeSubmit}
        isSubmitting={isSavingFeedback}
      />
      {backdropLoader.get("isLoading") && (
        <BackdropLoader
          content={backdropLoader.get("loaderMessage")}
        />
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    setBackdropLoader: bindActionCreators(setBackdropLoader, dispatch)
  };
};

const mapStateToProps = ({ app }) => {
  return {
    backdropLoader: app.get("backdropLoader")
  };
};

ShortsListModal.propTypes = {
  shorts: PropTypes.object,
  projectDetails: PropTypes.object,
  userDetails: PropTypes.object,
  params: PropTypes.object,
  setBackdropLoader: PropTypes.func,
  backdropLoader: PropTypes.bool
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(ShortsListModal));
